/**
 * TanStack Query hooks for the Google Drive import API.
 *
 * All API routes are under /api/auth/gdrive (mounted in backend/src/index.ts).
 *
 * Error shape from backend:
 *   { success: false, code: GDriveErrorCode, message: string }
 */

import { useQuery, useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import apiClient from '@/services/api';
import type {
  GDriveChannel,
  GDriveImportRequest,
  GDriveImportResult,
  GDriveFoldersResponse,
  GDriveUpdateFoldersRequest,
  GDriveUpdateFoldersResponse,
} from '@/types/gdrive';

// ─── Query keys ───────────────────────────────────────────────────────────────

export const GDRIVE_KEYS = {
  channels: ['gdrive-channels'] as const,
  fileMetadata: (fileId: string) => ['gdrive-file-metadata', fileId] as const,
  folders: (channelId: number) => ['gdrive-folders', channelId] as const,
} as const;

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * GET /api/auth/gdrive/channels
 * Returns the team's connected GDrive channels (tokens stripped server-side).
 */
export function useGDriveChannels(opts?: { enabled?: boolean }) {
  return useQuery<GDriveChannel[]>({
    queryKey: GDRIVE_KEYS.channels,
    queryFn: async () => {
      const response = await apiClient.get<{ success: true; data: GDriveChannel[] }>(
        '/api/auth/gdrive/channels',
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    // Skipped while Drive is locked by the plan (a background read would only 403)
    enabled: opts?.enabled ?? true,
  });
}

/**
 * GET /api/auth/gdrive  (mutation because it initiates an OAuth redirect)
 * Returns { authUrl, stateToken }. Caller must redirect browser to authUrl.
 *
 * Accepts an optional `teamId` (pass via `mutate(teamId)`). Required when the
 * caller has no resolvable team context (`useEffectiveTeamId().hasTeam ===
 * false` — e.g. agency staff with no home team, see CLAUDE.md "Active: User
 * Migration Testing" / SPEC §2). Callers should show a team picker first in
 * that case and pass the chosen team's id here; otherwise the backend 400s
 * with `NO_TEAM_CONTEXT`.
 */
export function useConnectGDrive() {
  return useMutation<string, Error, number | undefined>({
    mutationFn: async (teamId?: number) => {
      const response = await apiClient.get<{
        success: true;
        data: { authUrl: string; stateToken: string };
      }>('/api/auth/gdrive', {
        params: teamId ? { teamId } : undefined,
      });
      return response.data.data.authUrl;
    },
    onSuccess: (authUrl) => {
      window.location.href = authUrl;
    },
  });
}

/**
 * DELETE /api/auth/gdrive/channels/:channelId
 * Revokes tokens at Google and soft-deactivates the channel row.
 * Invalidates the channels cache and clears the cached Picker access token
 * so a subsequent click triggers a fresh OAuth flow rather than reusing a
 * (now invalid) token from sessionStorage.
 */
export function useDisconnectGDriveChannel() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (channelId: number) => {
      await apiClient.delete(`/api/auth/gdrive/channels/${channelId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GDRIVE_KEYS.channels });
      // Clear the Picker's session-cached access token. Even though the
      // backend revoked the channel's tokens, GIS still has a session-scoped
      // token cached on the frontend that would let the Picker open without
      // hitting our backend's auth-check.
      try {
        sessionStorage.removeItem('gdrive_picker_token');
      } catch {
        // sessionStorage unavailable — best-effort, ignore
      }
    },
  });
}

/**
 * POST /api/auth/gdrive/import
 * Validates the selected Drive files and creates deferred PostMedia rows.
 *
 * The backend allows partial success: valid files get rows created, invalid
 * files are returned in the `errors` array.
 */
export function useGDriveImport() {
  return useMutation<GDriveImportResult, Error, GDriveImportRequest>({
    mutationFn: async (request: GDriveImportRequest) => {
      const response = await apiClient.post<{
        success: true;
        data: GDriveImportResult;
      }>('/api/auth/gdrive/import', request);
      return response.data.data;
    },
  });
}

/**
 * GET /api/auth/gdrive/files/:fileId/metadata
 *
 * Lazy query — only runs when fileId is a non-empty string.
 * Useful for pre-import previews.
 */
export function useGDriveFileMetadata(fileId: string | null) {
  return useQuery({
    queryKey: GDRIVE_KEYS.fileMetadata(fileId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: true;
        data: {
          id: string;
          name: string;
          mimeType: string;
          size: number;
          thumbnailLink: string | null;
          webViewLink: string | null;
          iconLink: string | null;
          modifiedTime: string | null;
        };
      }>(`/api/auth/gdrive/files/${fileId}/metadata`);
      return response.data.data;
    },
    enabled: !!fileId && fileId.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: false,
  });
}

// ─── Folder scoping (docs/features/gdrive-folder-scoping/SPEC.md) ────────────

/**
 * GET /api/auth/gdrive/channels/:channelId/folders
 *
 * Readable by any team member. Always branch on `data.mode`, never on
 * `folders.length` — `mode: 'all'` (whole-Drive) also returns `folders: []`.
 * `mode: 'unconfigured'` means the caller must prompt the mode/folder-
 * selection step before this channel can be browsed (§5.1/§5.2, ACCESS-MODE
 * extension). `canEdit` tells the caller whether to render an editable or
 * read-only "Shared folders" section (§5.3, §7 — connector-only, strict).
 *
 * Lazy query — only runs when `channelId` is a positive number.
 */
export function useGDriveFolders(channelId: number | null, teamId?: number) {
  return useQuery<GDriveFoldersResponse>({
    queryKey: GDRIVE_KEYS.folders(channelId ?? 0),
    queryFn: async () => {
      const response = await apiClient.get<{ success: true; data: GDriveFoldersResponse }>(
        `/api/auth/gdrive/channels/${channelId}/folders`,
        { params: teamId ? { teamId } : undefined },
      );
      return response.data.data;
    },
    enabled: typeof channelId === 'number' && channelId > 0,
    staleTime: 60 * 1000, // 1 minute — folder scope changes are rare but should feel responsive
    retry: false,
  });
}

/**
 * PUT /api/auth/gdrive/channels/:channelId/folders
 *
 * Replaces the channel's access mode — either `{ mode: 'all' }` (whole Drive)
 * or `{ mode: 'scoped', folders }` (allow-list, >=1 folder). A bare `folders`
 * array (no `mode`) is accepted for back-compat and implies `'scoped'`.
 *
 * Connector-only on the backend (403 for everyone else, incl. team admins and
 * super-admins) — the frontend should gate the "add/remove"/"switch mode" UI
 * on `useGDriveFolders(...).data.canEdit` and never rely solely on hiding the
 * button.
 */
export type UpdateGDriveFoldersVariables = GDriveUpdateFoldersRequest & {
  channelId: number;
  teamId?: number;
};

export type UpdateGDriveFoldersMutation = UseMutationResult<
  GDriveUpdateFoldersResponse,
  Error,
  UpdateGDriveFoldersVariables
>;

export function useUpdateGDriveFolders(): UpdateGDriveFoldersMutation {
  const queryClient = useQueryClient();

  return useMutation<GDriveUpdateFoldersResponse, Error, UpdateGDriveFoldersVariables>({
    mutationFn: async (variables) => {
      const { channelId, teamId } = variables;
      const body =
        variables.mode === 'all'
          ? { mode: 'all' as const, ...(teamId ? { teamId } : {}) }
          : { mode: 'scoped' as const, folders: variables.folders, ...(teamId ? { teamId } : {}) };
      const response = await apiClient.put<{ success: true; data: GDriveUpdateFoldersResponse }>(
        `/api/auth/gdrive/channels/${channelId}/folders`,
        body,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: GDRIVE_KEYS.folders(variables.channelId) });
    },
  });
}

/**
 * Maps a GDrive import per-file error code to a short, user-facing message.
 * The backend's own message (per `GDriveFolderNotAllowedError`) is already
 * descriptive but references raw Drive file ids — this is the friendlier
 * client-owned copy for the common case surfaced in the import UI.
 *
 * GDRIVE_FOLDER_NOT_ALLOWED is commonly hit via the Picker's own search box:
 * `setParent(folderId)` scopes browsing/navigation to the shared folders, but
 * Google's Picker search always queries the WHOLE Drive regardless of the
 * active view's parent (a documented Picker limitation — there's no public
 * API to scope or disable search). The server is the real enforcement wall;
 * this message just needs to make that "why did this fail" moment clear.
 */
export function friendlyGDriveImportErrorMessage(code: string, fallbackMessage: string): string {
  if (code === 'GDRIVE_FOLDER_NOT_ALLOWED') {
    return "That file is outside your shared folders. Search can surface files from anywhere in Drive — pick one from inside a shared folder instead.";
  }
  return fallbackMessage;
}
